// Read-only Glaze replacement for `mini-apps/launcher/components/EventMemberSidebar.tsx`
// (plan 2026-09-24-005, U10 — "G: every `launcher:campaign` chip opens an
// in-page Glaze member sidebar for THAT contact + campaign"). Launcher keeps
// its own editable `EventMemberSidebar.tsx` for its own pages — retiring it
// is out of scope here. This component is v1 read-only (R11): no PATCH, no
// unsaved-state, so unlike the promoted `CampaignDetailSidebar.tsx` (which
// deliberately swallows Escape to protect unsaved edits) Escape closes this
// panel like any other dismissal.
//
// Shell (Root/Portal/Popup) copied from `CampaignDetailSidebar.tsx` — same
// non-modal `DialogPrimitive.Root`, Portal `relative z-50`, Popup `w-[480px]`
// dimensions/tokens, minus that file's escape-cancelling `onOpenChange`.
import { useQuery } from '@tanstack/react-query';
import { Dialog as DialogPrimitive } from '@base-ui/react';
import { Alert } from '@clay-run/glaze/alert';
import { Button, ButtonLink } from '@clay-run/glaze/button';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '@clay-run/glaze/description-list';
import { ModalHeader, ModalBody, ModalActions } from '@clay-run/glaze/modal';
import { SkeletonTextList } from '@clay-run/glaze/skeleton';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { useApi } from '../hooks';
import { MiniAppFetchError } from '../fetch-error';
import { fetchJson } from '../fetch-json';
import { LauncherContactMembershipSchema, type LauncherContactMembership } from '../launcher-member-schema';
import { campaignStatusBadge } from '../campaign-status-badge';
import { campaignManagerInLauncher } from '../flow-links';
import { Section } from './glaze-panel-helpers';

// ── Formatting helpers ───────────────────────────────────────────────────────

/** `event?.<field> ?? '—'` collapses two cases into one: `event === null`
 * (no event-manager row exists yet) and `event` present but this particular
 * field is null — both render the same placeholder, which is exactly the
 * "Event/Pre/Post sections show '—'" behavior the plan asks for when
 * `event` is null. */
const dash = (value: string | number | null | undefined): string =>
  value == null || value === '' ? '—' : String(value);

function yesNo(value: boolean | null | undefined): string {
  if (value == null) return '—';
  return value ? 'Yes' : 'No';
}

/** `arr_active_subscriptions` is a `NUMERIC(12,2)` — a Prisma `Decimal` on
 * the wire, i.e. `number | string`, per `launcher-member-schema.ts`. */
function formatArr(value: number | string | null): string {
  if (value == null) return '—';
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num)) return '—';
  return `$${num.toLocaleString()}`;
}

/** Date-only (`YYYY-MM-DD`) columns — parsed at LOCAL midnight so the
 * displayed day never shifts in negative-offset timezones (same pitfall
 * `mini-apps/launcher/utils/dates.ts`'s `formatEventDate` documents; not
 * imported from there to keep this `_shared` component self-contained). */
function formatDateOnly(value: string | null): string {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** `observedAt` is a full ISO timestamp (a `state_facts.observed_at` value), not a date-only string. */
function formatObservedAt(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Copy for the 404 case (no `launcher_sfdc_campaign_members` row synced for
 * this contact+campaign yet), keyed on `signalStatus` per the plan's "G copy
 * for 404" bullets:
 *  - `'Suggested'` — Terra hasn't invited the contact at all yet.
 *  - anything else (typically `'Invited'`) — Terra invited the contact
 *    locally but SFDC hasn't mirrored the Campaign Member row back yet; the
 *    date clause is omitted when `observedAt` is null.
 */
function notFoundCopy(signalStatus: string | null | undefined, observedAt: string | null | undefined): string {
  if (signalStatus === 'Suggested') {
    return 'Suggested for this campaign; no invite sent yet.';
  }
  const dateClause = observedAt ? ` on ${formatObservedAt(observedAt)}` : '';
  return `Invite sent from Terra${dateClause}; Salesforce has not mirrored the membership yet.`;
}

/** Never a raw status code (`.claude/team-conventions.md` / repo memory
 * "never show a rep a raw status code") — always the server's own
 * `userMessage` (from `problem()`'s `title`/`detail`), or a generic fallback
 * for the (untested, defensive-only) case of a non-HTTP failure such as a
 * response that fails `safeParse`. */
function errorMessage(err: unknown): string {
  if (err instanceof MiniAppFetchError && err.userMessage) return err.userMessage;
  return 'Failed to load campaign membership.';
}

// ── Component ────────────────────────────────────────────────────────────────

export interface CampaignMemberSidebarProps {
  campaignId: string;
  contactId: string;
  /** `state_facts.observed_at` for the triggering `launcher:campaign` fact — only used to date the 404 copy. */
  observedAt?: string | null;
  /** `state_facts.signal_status` for the triggering fact — only used to pick the 404 copy variant. */
  signalStatus?: string | null;
  onClose: () => void;
  /** Marketing users / admins get the "Open in Event Manager" escape hatch (R11 — read-only v1). */
  canManage: boolean;
}

export function CampaignMemberSidebar({
  campaignId,
  contactId,
  observedAt,
  signalStatus,
  onClose,
  canManage,
}: CampaignMemberSidebarProps) {
  const { apiFetch } = useApi();

  const { data, error, isLoading } = useQuery<LauncherContactMembership>({
    queryKey: ['launcher', 'campaigns', campaignId, 'member-by-contact', contactId],
    queryFn: () =>
      fetchJson(
        apiFetch,
        LauncherContactMembershipSchema,
        `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/members/by-contact/${encodeURIComponent(contactId)}`,
      ),
    retry: false,
    // The sidebar owns its own error presentation (the `Alert` branches
    // below cover both the expected 404 and any other error) — the global
    // `QueryCache.onError` toast (`makeQueryCacheOnError` in
    // `web/app-home/src/lib/mutation-cache-handler.ts`) must not also fire
    // for it (finding 3-4).
    meta: { suppressErrorToast: true },
  });

  const is404 = error instanceof MiniAppFetchError && error.status === 404;
  const isOtherError = !!error && !is404;

  const headerTitle = data ? `${dash(data.member.contact_name)} · ${data.campaign.name}` : 'Campaign Membership';
  const eventManagerHref = campaignManagerInLauncher(campaignId);

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={open => {
        if (!open) onClose();
      }}
      modal={false}
      disablePointerDismissal
    >
      {/* z-index lives on the Portal, never the Popup — see
          `CampaignDetailSidebar.tsx`'s identical comment for why (a nested
          Glaze popup opened inside this drawer is portaled into this same
          node by Base UI). */}
      <DialogPrimitive.Portal className="relative z-50">
        <DialogPrimitive.Popup className="fixed inset-y-0 right-0 flex w-[480px] flex-col border-l border-border-secondary bg-bg-primary shadow-lg">
          <ModalHeader onClose={onClose}>{headerTitle}</ModalHeader>

          <ModalBody className="flex-1 space-y-6 overflow-y-auto px-5 pt-4 pb-5" padded={false}>
            {isLoading && <SkeletonTextList lines={8} />}

            {!isLoading && is404 && <Alert status="info">{notFoundCopy(signalStatus, observedAt)}</Alert>}

            {!isLoading && isOtherError && <Alert status="error">{errorMessage(error)}</Alert>}

            {!isLoading && data && (
              <>
                {data.event === null && (
                  <Alert status="subtle">Event manager not initialised for this campaign</Alert>
                )}

                <Section title="Event Details">
                  <DescriptionList>
                    <DescriptionTerm>CM Status</DescriptionTerm>
                    <DescriptionDetails>
                      {(() => {
                        const badge = campaignStatusBadge(data.member.cm_status);
                        return <StatusBadge status={badge.status}>{badge.label}</StatusBadge>;
                      })()}
                    </DescriptionDetails>
                    <DescriptionTerm>Role</DescriptionTerm>
                    <DescriptionDetails>{dash(data.event?.role)}</DescriptionDetails>
                    <DescriptionTerm>Table</DescriptionTerm>
                    <DescriptionDetails>{dash(data.event?.table_name)}</DescriptionDetails>
                  </DescriptionList>
                </Section>

                <Section title="Pre-Event">
                  <DescriptionList>
                    <DescriptionTerm>Pre-Event Notes</DescriptionTerm>
                    <DescriptionDetails>{dash(data.event?.intel_notes)}</DescriptionDetails>
                    <DescriptionTerm>Dietary Restrictions</DescriptionTerm>
                    <DescriptionDetails>{dash(data.event?.dietary_restrictions)}</DescriptionDetails>
                    <DescriptionTerm>Owner Exclusion</DescriptionTerm>
                    <DescriptionDetails>{yesNo(data.event?.owner_exclusion)}</DescriptionDetails>
                    <DescriptionTerm>Owner Priority</DescriptionTerm>
                    <DescriptionDetails>{yesNo(data.event?.owner_priority)}</DescriptionDetails>
                    <DescriptionTerm>Cal Invite Sent</DescriptionTerm>
                    <DescriptionDetails>{yesNo(data.event?.cal_invite_sent)}</DescriptionDetails>
                    <DescriptionTerm>Cal Accepted</DescriptionTerm>
                    <DescriptionDetails>{yesNo(data.event?.cal_invite_accepted)}</DescriptionDetails>
                  </DescriptionList>
                </Section>

                <Section title="Post-Event">
                  <DescriptionList>
                    {/* Resolved names only — the raw SFDC user id
                        (`primary_met_with` et al.) is never rendered
                        (finding 3-3). Resolution happens server-side via
                        `LEFT JOIN salesforce_user`, per
                        `launcher-member-schema.ts`'s doc comment. */}
                    <DescriptionTerm>Met with Claymate 1</DescriptionTerm>
                    <DescriptionDetails>{dash(data.event?.primary_met_with_name)}</DescriptionDetails>
                    <DescriptionTerm>Met with Claymate 2</DescriptionTerm>
                    <DescriptionDetails>{dash(data.event?.secondary_met_with_name)}</DescriptionDetails>
                    <DescriptionTerm>Met with Claymate 3</DescriptionTerm>
                    <DescriptionDetails>{dash(data.event?.tertiary_met_with_name)}</DescriptionDetails>
                    <DescriptionTerm>Met With Notes</DescriptionTerm>
                    <DescriptionDetails>{dash(data.event?.goer_notes)}</DescriptionDetails>
                    <DescriptionTerm>Follow-up Owner</DescriptionTerm>
                    <DescriptionDetails>{dash(data.event?.follow_up_owner_name)}</DescriptionDetails>
                    <DescriptionTerm>Follow-up Date</DescriptionTerm>
                    <DescriptionDetails>{dash(data.event?.last_follow_up_date)}</DescriptionDetails>
                  </DescriptionList>
                </Section>

                <Section title="Account">
                  <DescriptionList>
                    <DescriptionTerm>Email</DescriptionTerm>
                    <DescriptionDetails>{dash(data.member.contact_email)}</DescriptionDetails>
                    <DescriptionTerm>Product Tier</DescriptionTerm>
                    <DescriptionDetails>{dash(data.member.product_tier)}</DescriptionDetails>
                    <DescriptionTerm>Plan Interval</DescriptionTerm>
                    <DescriptionDetails>{dash(data.member.plan_interval)}</DescriptionDetails>
                    <DescriptionTerm>ARR</DescriptionTerm>
                    <DescriptionDetails>{formatArr(data.member.arr_active_subscriptions)}</DescriptionDetails>
                    <DescriptionTerm>GS Owner</DescriptionTerm>
                    <DescriptionDetails>{dash(data.member.gs_owner_name)}</DescriptionDetails>
                    <DescriptionTerm>ClayDR</DescriptionTerm>
                    <DescriptionDetails>{dash(data.member.claydr_name)}</DescriptionDetails>
                    <DescriptionTerm>Last GTME Meeting</DescriptionTerm>
                    <DescriptionDetails>{formatDateOnly(data.member.last_gtme_meeting_date)}</DescriptionDetails>
                    <DescriptionTerm>Last GS Meeting</DescriptionTerm>
                    <DescriptionDetails>{formatDateOnly(data.member.last_gs_meeting_date)}</DescriptionDetails>
                    <DescriptionTerm>Last ClayDR Meeting</DescriptionTerm>
                    <DescriptionDetails>{formatDateOnly(data.member.last_claydr_meeting_date)}</DescriptionDetails>
                    {data.member.suppressed && (
                      <>
                        <DescriptionTerm>Contact Suppressed</DescriptionTerm>
                        <DescriptionDetails>{data.member.suppression_reasons ?? 'Yes'}</DescriptionDetails>
                      </>
                    )}
                  </DescriptionList>
                </Section>
              </>
            )}
          </ModalBody>

          <ModalActions>
            <div className="flex w-full items-center justify-end gap-3">
              {canManage && (
                <ButtonLink variant="secondary" href={eventManagerHref} target="_blank" rel="noopener noreferrer">
                  Open in Event Manager ↗
                </ButtonLink>
              )}
              <Button variant="secondary" size="lg" onClick={onClose}>
                Close
              </Button>
            </div>
          </ModalActions>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
